import React, { useMemo, useRef, forwardRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { ReactComponent as CalendarSearch } from "../../assets/images/CalendarSearch.svg";
import { ChevronDown } from "lucide-react";

function cx(...arr) {
  return arr.filter(Boolean).join(" ");
}

function fmt(d) {
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
}

/**
 * รองรับ 2 แบบ แต่ใช้ API เดิม:
 * - mode="range"  => range = [start, end]
 * - mode="single" => range = [date, date]
 */
export default function DateRangeButton({
  mode = "range",
  range,
  setRange,
  placeholder = "ช่วงเวลา",
  minDate,
  maxDate,
}) {
  const ref = useRef(null);
  const isRange = mode === "range";

  const [startDate, endDate] = Array.isArray(range) ? range : [null, null];

  const label = useMemo(() => {
    if (!startDate && !endDate) return placeholder;

    if (!isRange) {
      return startDate ? fmt(startDate) : placeholder;
    }

    if (startDate && !endDate) return `${fmt(startDate)} - ...`;
    return `${fmt(startDate)} - ${fmt(endDate)}`;
  }, [startDate, endDate, isRange, placeholder]);

  const CustomButton = forwardRef(function CustomButtonInner(
    { onClick },
    btnRef
  ) {
    return (
      <button
        ref={btnRef}
        type="button"
        onClick={onClick}
        className={cx(
          "inline-flex items-center gap-3",
          "rounded-xl px-5 py-3",
          "border-2 border-[#E5E7EB]",
          "text-sm font-semibold text-slate-900",
          "shadow-[0_1px_0_rgba(0,0,0,0.02)]",
          "hover:bg-[#EEEEEE] hover:border-[#EEEEEE] hover:text-[#047DED]",
          "transition"
        )}
      >
        <span className="inline-flex items-center justify-center">
          <CalendarSearch width={20} height={20} />
        </span>

        <span className="min-w-[84px] text-left">{label}</span>

        <ChevronDown size={18} />
      </button>
    );
  });

  if (isRange) {
    return (
      <div className="relative">
        <DatePicker
          ref={ref}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(update) => {
            setRange?.(update);

            const [s, e] = update || [];
            if (s && e) {
              ref.current?.setOpen(false);
            }
          }}
          isClearable={false}
          shouldCloseOnSelect={false}
          popperPlacement="bottom-end"
          popperClassName="z-[60]"
          dateFormat="dd/MM/yyyy"
          customInput={<CustomButton />}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <DatePicker
        ref={ref}
        selected={startDate}
        minDate={minDate}
        maxDate={maxDate}
        onChange={(date) => {
          if (date) {
            setRange?.([date, date]);
          } else {
            setRange?.([null, null]);
          }
          ref.current?.setOpen(false);
        }}
        isClearable={false}
        shouldCloseOnSelect
        popperPlacement="bottom-end"
        popperClassName="z-[60]"
        dateFormat="dd/MM/yyyy"
        customInput={<CustomButton />}
      />
    </div>
  );
}